import React from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../../components/text/Text';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useIsOnCard } from '../../../design/fieldSurface';

/**
 * SelectField: labelled field that shows the current choice and opens a picker sheet.
 * Props: label, valueLabel (shown text; placeholder when empty), placeholder, onPress, disabled, style,
 * showLabel (default true; false when the screen draws the label itself — it stays the accessible name)
 */
const SelectField = ({
	label,
	valueLabel,
	placeholder = 'Select',
	onPress,
	disabled = false,
	showLabel = true,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const isOnCard = useIsOnCard();
	const hasValue = Boolean(valueLabel);

	return (
		<View style={[styles.field, style]}>
			{showLabel && <Text style={styles.label}>{label}</Text>}
			<Pressable
				onPress={onPress}
				disabled={disabled}
				accessibilityRole="button"
				accessibilityLabel={`${label}, ${hasValue ? valueLabel : placeholder}`}
				accessibilityState={{ disabled }}
				style={({ pressed }) => [styles.control, isOnCard && styles.controlOnCard, disabled && styles.controlDisabled, pressed && styles.pressed]}
			>
				<Text
					style={[styles.value, !hasValue && styles.placeholder, disabled && styles.valueDisabled]}
					numberOfLines={1}
				>
					{hasValue ? valueLabel : placeholder}
				</Text>
				<Feather name="chevron-down" size={18} color={colors.textSecondary} />
			</Pressable>
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	field: {
		gap: 6,
	},
	label: {
		...type.fieldLabel,
		color: colors.text,
	},
	control: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.sm,
		// Same field as PickerButton / TextInput (44, 12 corners, chevron).
		minHeight: touch.min,
		paddingHorizontal: 14,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		backgroundColor: colors.card,
	},
	controlOnCard: {
		backgroundColor: colors.canvas,
	},
	// Keep the outline: disabledBg matches the card in Light.
	controlDisabled: {
		borderColor: colors.borderStrong,
		backgroundColor: colors.disabledBg,
	},
	pressed: {
		opacity: 0.85,
	},
	value: {
		...type.rowText,
		color: colors.text,
		flex: 1,
	},
	placeholder: {
		color: colors.textSecondary,
	},
	valueDisabled: {
		color: colors.disabledText,
	},
});

export default SelectField;
